<template>
    <div class="EntitiesEmptyState">
        <div class="EntitiesEmptyState__iconColumn">
            <img :src="addEntityImg" alt="Add entity">
        </div>
        <div class="EntitiesEmptyState__infoColumn">
            <div>
                <h1 class="EntitiesEmptyState__infoHeader">
                    You Haven’t Added Any Entities Yet
                </h1>
                <div class="EntitiesEmptyState__infoBody">
                    <p>
                        <span>You haven't connected to any entities. You can connect to entities individually or you can choose to bulk import your full list by downloading the</span>
                        <FontAwesomeIcon :icon="faFileExcel" class="text-eidTrustGreen" />
                        <NewButton
                            class="EntitiesEmptyState__csvButton"
                            :loading="loading"
                            :disabled="loading"
                            variant="link"
                            @click="downloadSampleCsv"
                        >
                            <span>CSV worksheet</span>
                        </NewButton>
                        <span>. Once you’re ready, you can come back and click</span>
                        <strong>
                            <FontAwesomeIcon :icon="faUpload" />
                            Bulk import
                        </strong>
                        <span> below to upload your entities.</span>
                    </p>
                </div>
                <RbacDisableGuard
                    v-slot="{ isDisabledByRbac }"
                    :features="[RbacFeature.dashboardAddAndEditEntity]"
                >
                    <div class="EntitiesEmptyState__actionRow">
                        <NewButton
                            type="primary"
                            class="EntitiesEmptyState__cta EntitiesEmptyState__cta--individual"
                            :icon="faPlusSquare"
                            :disabled="isDisabledByRbac"
                            @click="$emit('openAddIndividualEntityModal')"
                        >
                            Add Entity
                        </NewButton>
                        <NewButton
                            type="primary"
                            class="EntitiesEmptyState__cta EntitiesEmptyState__cta--bulk"
                            :icon="faUpload"
                            :disabled="isDisabledByRbac"
                            @click="$emit('openBulkImportEntitiesModal')"
                        >
                            Bulk Import
                        </NewButton>
                        <RbacDisplayGuard :features="[RbacFeature.dashboardUploadContract]">
                            <NewButton
                                v-if="showContractUploadButton"
                                type="primary"
                                class="EntitiesEmptyState__cta"
                                @click="$emit('openUploadContractModal')"
                            >
                                <EidIcon :icon-src="faUploadContract" />
                                Upload Contract
                            </NewButton>
                        </RbacDisplayGuard>
                    </div>
                </RbacDisableGuard>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { faFileExcel } from '@fortawesome/free-regular-svg-icons';
    import { faPlusSquare, faUpload } from '@fortawesome/free-solid-svg-icons';
    import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import RbacDisableGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableGuard.vue';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import NewButton from '@/components/new-button/NewButton.vue';
    import addEntityImg from '@/modules/dashboard/assets/add-entity-icon.svg';

    @Component({
        emits: [ 'downloadSampleCsv', 'openAddIndividualEntityModal', 'openBulkImportEntitiesModal', 'openUploadContractModal' ],
        components: { NewButton, FontAwesomeIcon, RbacDisableGuard, RbacDisplayGuard, EidIcon },
    })
    export default class EntitiesEmptyState extends Vue {
        private addEntityImg = addEntityImg;
        private faPlusSquare = faPlusSquare;
        private faFileExcel = faFileExcel;
        private faUpload = faUpload;
        private faUploadContract = faArrowUpFromBracket;

        @Prop({ type: Boolean, default: false })
        private loading!: boolean;

        @Prop({ type: Boolean, default: false })
        private showContractUploadButton!: boolean;

        private downloadSampleCsv() {
            this.$emit('downloadSampleCsv');
        }

        // Make RbacFeature available in template
        private RbacFeature = RbacFeature;
    }
</script>
